import React, { useState, useEffect } from 'react';
import type { Account, Campaign } from '../../store/appStore';
import { useAppStore } from '../../store/appStore';
import { DEMOKRATIUKA_COLORS, getCampaignTheme } from '../../lib/demokratiukaBrand';

interface Props {
  account: Account;
  campaign?: Campaign;
  onFollow: () => void;
}

type Variant = 'event-card' | 'three-steps' | 'minimal' | 'social-proof' | 'challenge';

const SLUG_VARIANT_MAP: Record<string, Variant> = {
  'kunstsilo-folkets-hus': 'event-card',
  'monarkiet-udemokratisk': 'three-steps',
  'strandsonen': 'minimal',
  'hvem-styrer-kristiansand': 'social-proof',
  'sorlandet-sekt': 'challenge',
  'skattepenger-kkg': 'challenge',
  'bistand': 'three-steps',
  'privatisere-utdanning-uia': 'minimal',
  'danby-choi-apning': 'event-card',
};

/* ─── Shared: campaign title hero block (themed per campaign) ─── */
const CampaignHero: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
  const theme = getCampaignTheme(campaign.slug);
  // Choose subtitle color — lightened accent for dark backgrounds, darker for light
  const mutedInk =
    theme.ink === '#ffffff'
      ? 'rgba(255,255,255,0.72)'
      : 'rgba(0,0,0,0.65)';
  return (
    <div className="px-4 sm:px-6 py-6 sm:py-8" style={{ backgroundColor: theme.bg }}>
      <p
        className="uppercase mb-3"
        style={{
          color: mutedInk,
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.1em',
        }}
      >
        Hva mener du?
      </p>
      <h2
        className="leading-tight"
        style={{
          color: theme.ink,
          fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
          fontWeight: 700,
          letterSpacing: '-0.01em',
        }}
      >
        {campaign.title}
      </h2>
      {campaign.description && (
        <p
          className="mt-3"
          style={{
            color: mutedInk,
            fontSize: '1rem',
            lineHeight: 1.7,
            fontWeight: 300,
          }}
        >
          {campaign.description}
        </p>
      )}
    </div>
  );
};

/* ─── Shared: CTA button ─── */
const FollowButton: React.FC<{
  cleanHandle: string;
  onFollow: () => void;
  label?: string;
}> = ({ cleanHandle, onFollow, label }) => (
  <button
    onClick={onFollow}
    className="w-full text-white transition-colors"
    style={{
      backgroundColor: '#000000',
      fontSize: '1rem',
      fontWeight: 700,
      padding: '1rem 1.5rem',
    }}
    onMouseOver={(e) =>
      (e.currentTarget.style.backgroundColor = DEMOKRATIUKA_COLORS.ytringsfrihet)
    }
    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#000000')}
  >
    {label || `Følg @${cleanHandle} på Instagram`}
  </button>
);

/* ═══════════════════════════════════════════
   VARIANT 1: Event-kort
   ═══════════════════════════════════════════ */
const EventCardVariant: React.FC<Props> = ({ account, campaign, onFollow }) => {
  const cleanHandle = account.instagramUrl.replace('@', '').trim();
  const theme = getCampaignTheme(campaign?.slug);
  return (
    <div>
      {campaign && <CampaignHero campaign={campaign} />}

      <div className="text-center px-4 sm:px-6 pt-6">
        <h1
          className="mb-4"
          style={{
            color: theme.bg,
            fontSize: 'clamp(1.5rem, 5vw, 2rem)',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Følg oss på Instagram for å si din mening!
        </h1>
        <p
          className="mb-3"
          style={{ color: '#222', fontSize: '1rem', lineHeight: 1.7, fontWeight: 400 }}
        >
          Følg <strong style={{ fontWeight: 600 }}>@{cleanHandle}</strong> på Instagram, og du er
          med i trekningen av <strong style={{ fontWeight: 600 }}>gratis billetter</strong> til
          showet:
        </p>

        <div
          className="my-6 p-4 sm:p-6 text-left"
          style={{ backgroundColor: '#ffffff', border: '2px solid #000000' }}
        >
          <p style={{ color: '#000000', fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.3 }}>
            Falske nyheter og ekte konsekvenser
          </p>
          <p
            className="mt-2"
            style={{ color: '#666', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.7 }}
          >
            Med Hilde Sandvik, Thomas Seltzer, Danby Choi m.fl.
          </p>
          <p style={{ color: '#666', fontSize: '0.875rem', fontWeight: 400 }}>
            Lørdag 2. mai &middot; Teateret, hovedscenen &middot; 19:00-20:00
          </p>
        </div>

        <p
          className="mb-6"
          style={{ color: '#666', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.7 }}
        >
          Du blir sendt til Instagram når du trykker. Kom tilbake hit etterpå for å si din mening.
        </p>

        <FollowButton cleanHandle={cleanHandle} onFollow={onFollow} />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   VARIANT 2: 3-stegs flow
   ═══════════════════════════════════════════ */
const ThreeStepsVariant: React.FC<Props> = ({ account, campaign, onFollow }) => {
  const cleanHandle = account.instagramUrl.replace('@', '').trim();
  const theme = getCampaignTheme(campaign?.slug);
  return (
    <div>
      {campaign && <CampaignHero campaign={campaign} />}

      <div className="px-4 sm:px-6 py-5 sm:py-6" style={{ backgroundColor: '#ffffff' }}>
        <h1
          className="mb-2"
          style={{
            color: '#000',
            fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Si din mening. Den blir faktisk lest.
        </h1>
        <p style={{ color: '#444', fontSize: '0.9375rem', lineHeight: 1.6, fontWeight: 400 }}>
          Demokratiuka samler alle svar og legger dem frem for politikere og beslutningstakere i
          Kristiansand.
        </p>
      </div>

      <div className="px-4 sm:px-6 py-5 sm:py-6" style={{ borderTop: '1px solid #e5e5e5' }}>
        <p
          className="uppercase mb-4"
          style={{
            color: '#999',
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
          }}
        >
          Slik fungerer det
        </p>

        {[
          { n: 1, title: `Følg @${cleanHandle} på Instagram`, sub: 'Tar 5 sekunder' },
          { n: 2, title: 'Kom tilbake hit', sub: 'Trykk tilbake-knappen i nettleseren' },
          {
            n: 3,
            title: 'Skriv hva du mener',
            sub: 'Svaret ditt blir levert til de som kan gjøre noe med det',
          },
        ].map((step) => (
          <div className="flex items-start gap-4 mb-4" key={step.n}>
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                backgroundColor: theme.bg,
                color: theme.ink,
                fontSize: '0.875rem',
                fontWeight: 700,
              }}
            >
              {step.n}
            </div>
            <div>
              <p style={{ color: '#000', fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.3 }}>
                {step.title}
              </p>
              <p style={{ color: '#666', fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 }}>
                {step.sub}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 sm:px-6 pb-6">
        <FollowButton cleanHandle={cleanHandle} onFollow={onFollow} />
        <p
          className="mt-3 text-center"
          style={{ color: '#999', fontSize: '0.75rem', fontWeight: 400 }}
        >
          Du blir sendt til Instagram. Kom rett tilbake for å si din mening.
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   VARIANT 3: Minimalist
   ═══════════════════════════════════════════ */
const MinimalVariant: React.FC<Props> = ({ account, campaign, onFollow }) => {
  const cleanHandle = account.instagramUrl.replace('@', '').trim();
  return (
    <div>
      {campaign && <CampaignHero campaign={campaign} />}

      <div className="px-4 sm:px-6 py-6 sm:py-8">
        <p
          className="mb-8"
          style={{ color: '#000', fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.6 }}
        >
          Din mening teller. Følg oss på Instagram, kom tilbake, og si hva du mener. Svaret ditt
          blir lagt frem for de som bestemmer.
        </p>

        <FollowButton cleanHandle={cleanHandle} onFollow={onFollow} />

        <p
          className="mt-4 text-center"
          style={{ color: '#999', fontSize: '0.75rem', fontWeight: 400 }}
        >
          Etter du har fulgt, kom tilbake hit for å skrive svaret ditt.
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   VARIANT 4: Sosial proof
   ═══════════════════════════════════════════ */
const SocialProofVariant: React.FC<Props & { responseCount: number }> = ({
  account,
  campaign,
  onFollow,
  responseCount,
}) => {
  const cleanHandle = account.instagramUrl.replace('@', '').trim();
  const theme = getCampaignTheme(campaign?.slug);
  return (
    <div>
      {campaign && <CampaignHero campaign={campaign} />}

      <div className="px-4 sm:px-6 py-5 sm:py-6">
        <div
          className="mb-6 p-5 text-center"
          style={{ backgroundColor: '#ffffff', border: `2px solid ${theme.bg}` }}
        >
          <p
            style={{
              color: theme.bg,
              fontSize: 'clamp(2rem, 6vw, 3rem)',
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {responseCount}
          </p>
          <p
            className="mt-1"
            style={{ color: theme.bg, fontSize: '0.9375rem', fontWeight: 600 }}
          >
            {responseCount === 1 ? 'person' : 'personer'} har allerede sagt sin mening
          </p>
        </div>

        <p
          className="mb-6"
          style={{ color: '#444', fontSize: '0.9375rem', lineHeight: 1.6, fontWeight: 400 }}
        >
          Følg <strong style={{ fontWeight: 600 }}>@{cleanHandle}</strong> på Instagram og bli en av
          dem. Alle svar samles og legges frem for politikerne.
        </p>

        <FollowButton cleanHandle={cleanHandle} onFollow={onFollow} />
        <p
          className="mt-3 text-center"
          style={{ color: '#999', fontSize: '0.75rem', fontWeight: 400 }}
        >
          Følg, kom tilbake, si din mening.
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   VARIANT 5: Provokasjon / utfordring
   ═══════════════════════════════════════════ */
const ChallengeVariant: React.FC<Props> = ({ account, campaign, onFollow }) => {
  const cleanHandle = account.instagramUrl.replace('@', '').trim();
  const theme = getCampaignTheme(campaign?.slug);
  const mutedInk =
    theme.ink === '#ffffff' ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.65)';
  return (
    <div>
      {campaign && (
        <div
          className="px-4 sm:px-6 py-8 sm:py-10"
          style={{ backgroundColor: theme.bg }}
        >
          <h2
            className="leading-tight"
            style={{
              color: theme.ink,
              fontSize: 'clamp(1.75rem, 6vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
            }}
          >
            {campaign.title}
          </h2>
          {campaign.description && (
            <p
              className="mt-3"
              style={{ color: mutedInk, fontSize: '1rem', lineHeight: 1.7, fontWeight: 300 }}
            >
              {campaign.description}
            </p>
          )}
        </div>
      )}

      <div className="px-4 sm:px-6 py-5 sm:py-6">
        <h1
          className="mb-3"
          style={{
            color: '#000',
            fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Tør du si hva du egentlig mener?
        </h1>
        <p
          className="mb-2"
          style={{ color: '#444', fontSize: '0.9375rem', lineHeight: 1.6, fontWeight: 400 }}
        >
          De fleste tier. Noen få sier ifra. Svarene er anonyme og blir lagt frem for politikere og
          beslutningstakere i Kristiansand.
        </p>
        <p
          className="mb-6"
          style={{ color: '#666', fontSize: '0.8125rem', lineHeight: 1.5, fontWeight: 400 }}
        >
          Alt du trenger å gjøre er å følge oss på Instagram og komme tilbake.
        </p>

        <FollowButton
          cleanHandle={cleanHandle}
          onFollow={onFollow}
          label="Jeg tør. Følg på Instagram"
        />
        <p
          className="mt-3 text-center"
          style={{ color: '#999', fontSize: '0.75rem', fontWeight: 400 }}
        >
          Du blir sendt til Instagram. Kom rett tilbake for å skrive svaret ditt.
        </p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   Main component — picks variant by slug
   ═══════════════════════════════════════════ */
export const DemokratiukaFollowGate: React.FC<Props> = (props) => {
  const { campaign } = props;
  const variant = (campaign?.slug && SLUG_VARIANT_MAP[campaign.slug]) || 'three-steps';
  const { fetchCampaignSuggestions } = useAppStore();

  const [responseCount, setResponseCount] = useState(0);

  useEffect(() => {
    if (variant === 'social-proof' && campaign) {
      fetchCampaignSuggestions(campaign.id)
        .then((suggestions) => setResponseCount(suggestions.length))
        .catch(() => setResponseCount(0));
    }
  }, [variant, campaign?.id]);

  switch (variant) {
    case 'event-card':
      return <EventCardVariant {...props} />;
    case 'three-steps':
      return <ThreeStepsVariant {...props} />;
    case 'minimal':
      return <MinimalVariant {...props} />;
    case 'social-proof':
      return <SocialProofVariant {...props} responseCount={responseCount} />;
    case 'challenge':
      return <ChallengeVariant {...props} />;
    default:
      return <ThreeStepsVariant {...props} />;
  }
};
